import React, { Component } from 'react';
import PropTypes from 'prop-types';
import AutoComplete from 'material-ui/AutoComplete';
import Chip from 'material-ui/Chip';
import RaisedButton from 'material-ui/RaisedButton';
import FlatButton from 'material-ui/FlatButton';
import { teal300, red300 } from 'material-ui/styles/colors';

class MultiChipInput extends Component {
  constructor(props) {
    super(props);
    this.handleUpdateInput = this.handleUpdateInput.bind(this);
    this.handleNewRequest = this.handleNewRequest.bind(this);
    this.handleDeleteRequest = this.handleDeleteRequest.bind(this);
    this.handleSelectAll = this.handleSelectAll.bind(this);
    this.handleDeleteAll = this.handleDeleteAll.bind(this);

    /*
     * Bind input's precedence is higher than default input
     * Default input will only show once
     */
    const { bindInput, defaultInput } = this.props;
    const state = { searchText: '' };
    if (bindInput) {
      state.selected = new Set(bindInput);
    } else if (defaultInput) {
      state.selected = new Set(defaultInput);
    } else {
      state.selected = new Set();
    }
    this.state = state;
  }

  handleUpdateInput(searchText) {
    this.setState({ searchText });
  }

  handleNewRequest() {
    const { searchText } = this.state;
    let { selected } = this.state;
    const { bindInput, onChipCreate, onError } = this.props;

    selected = bindInput ? (new Set(bindInput)) : selected;

    const newState = { ...this.state, searchText: '' };

    if (selected.has(searchText)) {
      onError({ error: 'Data already being selected' });
    } else if (new Set(this.props.data).has(searchText)) {
      newState.selected = selected.add(searchText);

      /* Invoke the onChipCreate event */
      onChipCreate({ created: searchText, results: Array.from(selected) });
    } else {
      onError({ error: 'Data Not Found' });
    }

    this.setState(newState);
  }

  handleDeleteRequest(key) {
    let { selected } = this.state;
    const { bindInput, onChipDelete } = this.props;

    selected = bindInput ? (new Set(bindInput)) : selected;

    selected.delete(key);
    this.setState({ selected });

    /* Invoke the onChipDelete event */
    onChipDelete({ deleted: key, results: Array.from(selected) });
  }

  handleSelectAll() {
    const { bindInput, data, onSelectAll } = this.props;
    let { selected } = this.state;

    selected = bindInput ? (new Set(bindInput)) : selected;

    const newlySelected = data.filter(key => !selected.has(key));
    this.setState({ selected: new Set(this.props.data) });
    onSelectAll({ selected: newlySelected, results: [...this.props.data] });
  }

  handleDeleteAll() {
    const { bindInput, onDeleteAll } = this.props;
    let { selected } = this.state;

    selected = bindInput ? (new Set(bindInput)) : selected;

    this.setState({ selected: new Set() });
    onDeleteAll({ deleted: Array.from(selected), results: [] });
  }

  render() {
    const {
      label,
      data,
      enableDeleteAll,
      enableSelectAll,
      maxSearchResults,
      bindInput,
      autoCompleteStyle,
      chipStyle,
      chipsWrapperStyle,
      buttonGroupStyle,
      flatButtonStyle,
      raisedButtonStyle,

      /* Override the settings according to the Material UI Style-guide */
      autoCompleteConfig,
      chipConfig,
      raisedButtonConfig,
      flatButtonConfig
    } = this.props;

    const { searchText } = this.state;
    let { selected } = this.state;

    /* If bindInput exist it will override the selected state */
    selected = bindInput ? (new Set(bindInput)) : selected;

    const disableDeleteAll = selected.size === 0;
    const renderButtonGroup = (
      <div className="button-group" style={buttonGroupStyle}>
        {
          enableSelectAll &&
          <RaisedButton
            backgroundColor={teal300}
            buttonStyle={raisedButtonStyle}
            onClick={this.handleSelectAll}
            {...raisedButtonConfig}
          >
            Select All
          </RaisedButton>
        }

        {
          enableDeleteAll &&
          <FlatButton
            backgroundColor="#666"
            style={{ ...flatButtonStyle, color: disableDeleteAll ? '#aaa' : flatButtonStyle.color }}
            hoverColor={red300}
            disabled={disableDeleteAll}
            onClick={this.handleDeleteAll}
            {...flatButtonConfig}
          >
            Delete All
          </FlatButton>
        }
      </div>
    );

    return (
      <div className="MultiChip">
        <AutoComplete
          filter={AutoComplete.caseInsensitiveFilter}
          maxSearchResults={maxSearchResults}
          floatingLabelText={label}
          dataSource={data}
          searchText={searchText}
          onUpdateInput={this.handleUpdateInput}
          onNewRequest={this.handleNewRequest}

          {...autoCompleteStyle}
          {...autoCompleteConfig}
        />
        <div className="chips-wrapper" style={chipsWrapperStyle}>
          {
            Array.from(selected).map((chip, i) => (
              <Chip
                key={i}
                onRequestDelete={() => this.handleDeleteRequest(chip)}
                style={chipStyle}
                {...chipConfig}
              >
                {chip}
              </Chip>
            ))
          }
        </div>

        { renderButtonGroup }
      </div>
    );
  }
}

MultiChipInput.propTypes = {
  label: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.string).isRequired,
  maxSearchResults: PropTypes.number,
  enableDeleteAll: PropTypes.bool,
  enableSelectAll: PropTypes.bool,
  onChipCreate: PropTypes.func,
  onChipDelete: PropTypes.func,
  onSelectAll: PropTypes.func,
  onDeleteAll: PropTypes.func,
  onError: PropTypes.func,
  chipStyle: PropTypes.object,
  chipsWrapperStyle: PropTypes.object,
  buttonGroupStyle: PropTypes.object,
  flatButtonStyle: PropTypes.object,
  raisedButtonStyle: PropTypes.object,
  autoCompleteStyle: PropTypes.object,

  /* defaultInput will only show once when component mounted */
  defaultInput: PropTypes.arrayOf(PropTypes.string),

  /*
   *  bindInput will override the innerstate of showing chips
   *  whenever adding chips or deleting chips
   */
  bindInput: PropTypes.arrayOf(PropTypes.string),

  /* Override the settings according to the Material UI Style-guide */
  autoCompleteConfig: PropTypes.object,
  chipConfig: PropTypes.object,
  raisedButtonConfig: PropTypes.object,
  flatButtonConfig: PropTypes.object
};

MultiChipInput.defaultProps = {
  enableSelectAll: true,
  enableDeleteAll: true,
  maxSearchResults: 5,
  defaultInput: null,
  bindInput: null,
  onChipCreate() { },
  onChipDelete() { },
  onSelectAll() { },
  onDeleteAll() { },
  onError(error) { console.log(error); },
  // autoCompleteStyle: {
  //   display: 'inline-block',
  //   marginRight: '5pt',
  //   marginBottom: '5pt',
  //   fontFamily: "'Quicksand', sans-serif",
  //   backgroundColor: '#eee'
  // },
  autoCompleteStyle: {
    underlineStyle: { borderColor: '#888' },
    underlineFocusStyle: { borderColor: '#ddd' },
    floatingLabelStyle: { color: 'white' },
    inputStyle: { color: 'white' },
    fullWidth: true
  },
  chipStyle: {
    display: 'inline-block',
    marginRight: '5pt',
    marginBottom: '5pt',
    fontFamily: "'Quicksand', sans-serif",
    backgroundColor: '#eee'
  },
  chipsWrapperStyle: {
    padding: '5pt 0',
    marginBottom: '10pt',
    whiteSpace: 'normal'
  },
  buttonGroupStyle: {
    textAlign: 'right',
    marginBottom: '10pt'
  },
  flatButtonStyle: {
    color: 'white',
    marginLeft: '5pt',
    fontSize: '10pt'
  },
  raisedButtonStyle: {
    color: 'white',
    fontSize: '10pt'
  },
  autoCompleteConfig: {},
  chipConfig: {
    labelStyle: {
      verticalAlign: 'top',
      fontWeight: 400
    }
  },
  raisedButtonConfig: {},
  flatButtonConfig: {}
};

export default MultiChipInput;



// WEBPACK FOOTER //
// ./src/components/MultiChipInput.jsx